/*
 * Copyright 2026 Sony Corporation
 */

import { z } from 'zod';

/**
 * The asset library's second source (devplan §5.5).
 *
 * Poly Haven is reached straight from the browser; Sketchfab is not, and the
 * difference is not CORS — their search does send our origin back. It is that
 * the licence filter has to be ours to set. A browser that composes its own
 * query can ask for `by-nc-nd`, and §5.6's save step bakes whatever it gets
 * into a published `.babylon`. Enforced here, the set of acceptable licences
 * is a property of the deployment rather than a habit of one component.
 */

/** Sketchfab's own id: 32 lower-case hex, and checked before it reaches a URL. */
export const SketchfabUidSchema = z.string().regex(/^[0-9a-f]{32}$/, 'must be a Sketchfab uid');

/**
 * The licences this service will search.
 *
 * **PoC setting.** §5.6's rule is that a source must be embeddable in a saved
 * `.babylon` and redistributable with it, which only CC0 satisfies outright:
 * the CC BY family needs a credit the baked file cannot carry, `by-nc-sa` also
 * forbids commercial use and forces the same licence on anything it is
 * combined with, and the Standard licences do not permit redistribution at
 * all. Kept as a list, and enforced here, so narrowing it back to `['cc0']`
 * before this is anything but a demonstration is a one-line change in one
 * place.
 *
 * `st` yields nothing while `downloadable=true` stands — Standard-licence
 * models are store purchases — but it is listed because it was asked for, and
 * because its absence would otherwise look like an oversight.
 */
export const SKETCHFAB_LICENSES = ['cc0', 'by', 'by-nc-sa', 'st', 'free-st'] as const;

/** Sketchfab caps a page at 24 regardless of what is asked for. */
export const SKETCHFAB_PAGE = 24;

export const SketchfabAssetSchema = z.object({
  uid: SketchfabUidSchema,
  name: z.string(),
  /** The author's username. CC0 obliges nobody, but the other licences here do (devplan §5.8). */
  author: z.string(),
  /** Their label for it, shown on the tile: what may be done with the model is not uniform any more. */
  license: z.string().optional(),
  thumbnail_url: z.string(),
  /** Their page for the model, which is where a credit should point. */
  viewer_url: z.string(),
  /** What an import would cost, so the grid can say so before anyone waits for it. */
  bytes: z.number().int().nonnegative().optional(),
  face_count: z.number().int().nonnegative().optional(),
});
export type SketchfabAsset = z.infer<typeof SketchfabAssetSchema>;

export const SketchfabSearchResponseSchema = z.object({
  assets: z.array(SketchfabAssetSchema),
  /** Opaque; hand it back to page on. Absent at the end of the results. */
  cursor: z.string().optional(),
});
export type SketchfabSearchResponse = z.infer<typeof SketchfabSearchResponseSchema>;

export const SketchfabSearchQuerySchema = z.object({
  q: z.string().max(120).optional(),
  cursor: z.string().max(200).optional(),
});
export type SketchfabSearchQuery = z.infer<typeof SketchfabSearchQuerySchema>;
